<script lang="ts">
export default {
  name: 'KkSearchQueryTable',
}
</script>

<script lang="ts" setup>
import { ref, computed, useCssModule, onMounted, onBeforeUnmount } from 'vue'
import { useStore } from 'vuex'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { KkIcon, KkBadge, KkLoading } from '../../../../components/base'
import { generateGraphId } from '../../../../modules/analysis'
import { KkGrowthRate, KkLabelBrandedBadge } from '../..'
import type { SearchKeywordImpressionAggregate } from 'src/biz_spa/types'
import type { ViewType } from '../../types'

export type Sort = {
  sortKey: string
  sortOrder: 'asc' | 'desc' | ''
}

export interface Props {
  items: SearchKeywordImpressionAggregate[]
  all: SearchKeywordImpressionAggregate
  graph: string[]
  totalCount: number
  comparison?: boolean
  term?: string
  comparisonTerm?: string
  sort?: Sort
  viewType?: ViewType
  loading?: boolean
}

export interface Emits {
  (key: 'update:graph', value: Props['graph']): void
  (key: 'update:sort', value: Props['sort']): void
}

const props = withDefaults(defineProps<Props>(), { viewType: 'greaterThan15' })
const emits = defineEmits<Emits>()
const route = useRoute()
const { t, te } = useI18n()
const styles = useCssModule()
const store = useStore()

const container = ref<HTMLDivElement>()
const containerWidth = ref<{ client: number; scroll: number }>({ client: 0, scroll: 0 })
const scrollInterval = ref<number>()
const scrollLeft = ref(0)

const graphModel = computed({
  get: () => props.graph,
  set: (value) => emits('update:graph', value),
})

const sortModel = computed({
  get: () => props.sort,
  set: (value) => emits('update:sort', value),
})

const inboundOption = computed(() => store.getters['companiesCompany/inboundOption'])
const showOnlyOwnerAuthority = computed<boolean>(() => store.getters['companiesAccessibleApi/showOnlyOwnerAuthority'])
const companyUuid = computed(() => route.params.company_uuid as string)

const enableController = computed(() => containerWidth.value.scroll > containerWidth.value.client)
const maxScrollValue = computed(() => containerWidth.value.scroll - containerWidth.value.client)

const getContainerWidth = () => {
  if (!container.value) return
  containerWidth.value = {
    client: container.value.clientWidth,
    scroll: container.value.scrollWidth,
  }
}

const getSortClasses = (key: string) => {
  if (!sortModel.value) return []
  const classes: string[] = [styles.sort]
  if (sortModel.value.sortKey !== key) return classes
  classes.push(styles[sortModel.value.sortOrder])
  return classes
}

const getLocale = (aggregate: SearchKeywordImpressionAggregate) => {
  if (!aggregate.locale && !aggregate.originalLocale) return ''
  if (aggregate.locale !== 'others') return t(`locales.${aggregate.locale}`)
  if (te(`locales.${aggregate.originalLocale}`)) return `${t('locales.others')}（${t(`locales.${aggregate.originalLocale}`)}）`
  return t('locales.others')
}

const valueFormatter = (count: number | null) => {
  if (!count) return '0'
  if (count > 0) return count.toLocaleString()
  return '< 15'
}

const handleClickClear = () => {
  graphModel.value = [generateGraphId(props.all)]
}

const updateScroll = (value: number) => {
  if (!container.value) return
  scrollInterval.value = window.setInterval(() => {
    if (!container.value) return
    scrollLeft.value = container.value.scrollLeft + value
    container.value.scrollLeft = scrollLeft.value
  }, 10)
}

const cancelScroll = () => {
  window.clearInterval(scrollInterval.value)
}

const resizeCullTime = ref<number>()
const handleResize = () => {
  window.clearTimeout(resizeCullTime.value)
  resizeCullTime.value = window.setTimeout(() => {
    getContainerWidth()
  }, 80)
}

const handleSort = () => {
  if (!sortModel.value) return
  if (sortModel.value.sortOrder === '') {
    sortModel.value.sortOrder = 'desc'
  } else {
    sortModel.value.sortOrder = sortModel.value.sortOrder === 'asc' ? 'desc' : 'asc'
  }
  sortModel.value.sortKey = 'impressionCount'
}

onMounted(() => {
  // MEMO: 少し待たないと正しい値が取れない
  window.setTimeout(() => {
    getContainerWidth()
  }, 400)
  window.addEventListener('resize', handleResize)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', handleResize)
})
</script>

<template>
  <KkLoading v-if="props.loading" />
  <div v-else :class="$style.root">
    <button type="button" :class="$style.clear" @click="handleClickClear">{{ $t('pages.companies.analysis.table.clearSelection') }}</button>
    <div v-if="props.items" ref="container" :class="$style.container">
      <table :class="$style.table">
        <colgroup>
          <col style="min-width: 310px">
        </colgroup>
        <thead :class="$style.header">
          <tr>
            <th :rowspan="props.comparison ? '2' : undefined">
              {{ $t('models.analysis.crossAnalysisTypes.searchQuery.name') }}（{{ props.totalCount }}）
            </th>
            <th :rowspan="props.comparison ? '2' : undefined">{{ $t('models.analysis.searchQueriesDataType.searchKeywordLabelBranded.name') }}</th>
            <th :rowspan="props.comparison ? '2' : undefined">{{ $t('models.analysis.searchQueriesDataType.searchKeywordLabelSelected.name') }}</th>
            <th v-if="inboundOption" :rowspan="props.comparison ? '2' : undefined">{{ $t('models.analysis.searchQueriesDataType.locale.name') }}</th>
            <th :colspan="props.comparison ? '3 ' : undefined">
              <template v-if="props.comparison">{{ $t('models.analysis.searchQueriesDataType.impressionCount.name') }}</template>
              <button v-else type="button" :class="getSortClasses('impressionCount')" @click="handleSort">
                {{ $t('models.analysis.searchQueriesDataType.impressionCount.name') }}
              </button>
            </th>
          </tr>
          <tr v-if="props.comparison">
            <th>{{ props.comparisonTerm }}</th>
            <th><button type="button" :class="getSortClasses('impressionCount')" @click="handleSort">{{ props.term }}</button></th>
            <th>{{ $t('models.analysis.searchQueriesDataType.growthRate.name') }}</th>
          </tr>
          <tr :class="$style.divider" />
        </thead>

        <tbody>
          <tr>
            <td :class="$style.head">
              <div v-if="props.all" class="c-checkbox">
                <div class="c-checkbox__item">
                  <input id="items-head" v-model="graphModel" :value="generateGraphId(props.all)" type="checkbox" class="c-checkbox__input">
                  <label for="items-head" class="c-checkbox__label">{{ $t('labels.all') }}</label>
                </div>
              </div>
            </td>
            <td :class="$style.head" class="u-taR">
              <template v-if="props.all && props.all.searchKeywordLabelBranded?.length">
                <div v-for="label in props.all.searchKeywordLabelBranded" :key="label.name" :class="$style.total">
                  <KkLabelBrandedBadge :kind="label.name" :class="$style.tag" />
                  <div :class="$style.countWithRate">
                    <strong :class="$style.count">{{ label.impressionCount ? label.impressionCount.toLocaleString() : '0' }}</strong>
                    <span :class="$style.rate">（{{ label.rate || 0 }}%）</span>
                  </div>
                </div>
                <div :class="$style.text">{{ $t('models.analysis.searchQueriesDataType.searchKeywordLabelBranded.text') }}</div>
              </template>
              <template v-else>
                <div>-</div>
                <router-link
                  v-if="showOnlyOwnerAuthority"
                  :to="{ name: 'CompaniesSearchKeywordImpressionLabelForm', params: { company_uuid: companyUuid, label_id: 'branded' } }"
                  :class="$style.link"
                >
                  {{ $t('models.analysis.searchQueriesDataType.searchKeywordLabelBranded.link') }}
                </router-link>
              </template>
            </td>
            <td :class="$style.head" class="u-taR">
              <template v-if="props.all && props.all.searchKeywordLabelSelected?.length">
                <div :class="$style.countWithRate">
                  <strong :class="$style.count">
                    {{ props.all.searchKeywordLabelSelected[0].impressionCount ? props.all.searchKeywordLabelSelected[0].impressionCount.toLocaleString() : '0' }}
                  </strong>
                  <span :class="$style.rate">（{{ props.all.searchKeywordLabelSelected[0].rate || 0 }}%）</span>
                </div>
                <div :class="$style.text">{{ $t('models.analysis.searchQueriesDataType.searchKeywordLabelSelected.text') }}</div>
              </template>
              <template v-else>
                <div>-</div>
                <router-link
                  v-if="showOnlyOwnerAuthority"
                  :to="{ name: 'CompaniesSearchKeywordImpressionLabelForm', params: { company_uuid: companyUuid, label_id: 'selected' } }"
                  :class="$style.link"
                >
                  {{ $t('models.analysis.searchQueriesDataType.searchKeywordLabelSelected.link') }}
                </router-link>
              </template>
            </td>
            <td v-if="inboundOption" :class="$style.head" />
            <td v-if="props.comparison" :class="$style.head">
              <strong :class="$style.count">
                {{ props.all && props.all.comparisonImpressionCount ? props.all.comparisonImpressionCount.toLocaleString() : '0' }}
              </strong>
              <div :class="$style.text">{{ $t('models.analysis.searchQueriesDataType.impressionCount.text') }}<br>（{{ props.comparisonTerm }}）</div>
            </td>
            <td :class="$style.head" class="u-taR">
              <strong v-if="props.all" :class="$style.count">{{ props.all.impressionCount.toLocaleString() }}</strong>
              <div :class="$style.text">
                {{ $t('models.analysis.searchQueriesDataType.impressionCount.text') }}
                <template v-if="props.comparison"><br>（{{ props.term }}）</template>
              </div>
            </td>
            <td v-if="props.comparison" :class="$style.head" class="u-taR">
              <div class="u-taR">
                <KkGrowthRate v-if="props.all && props.all.growthRate" :rate="props.all.growthRate" size="md" />
                <span v-else :class="$style.count">0</span>
              </div>
              <div :class="$style.text">
                {{ $t('models.analysis.searchQueriesDataType.growthRate.name') }}
              </div>
            </td>
          </tr>

          <tr v-for="(item, index) in props.items" :key="item.fields[0].id">
            <th>
              <div class="c-checkbox">
                <div class="c-checkbox__item">
                  <input :id="`item-${index}`" v-model="graphModel" :value="generateGraphId(item)" type="checkbox" class="c-checkbox__input">
                  <label :for="`item-${index}`" class="c-checkbox__label">
                    <span :class="$style.number">{{ index + 1 }}.</span>{{ item.fields[0].id }}
                  </label>
                </div>
              </div>
            </th>
            <td class="u-taC">
              <template v-if="item.searchKeywordLabelBranded === null">-</template>
              <KkLabelBrandedBadge v-else :kind="item.searchKeywordLabelBranded ? $t('models.analysis.searchQueriesDataType.searchKeywordLabelBranded.branded') : $t('models.analysis.searchQueriesDataType.searchKeywordLabelBranded.nonBranded')" />
            </td>
            <td class="u-taC">
              <template v-if="!item.searchKeywordLabelSelected">-</template>
              <KkBadge v-else :text="$t('models.analysis.searchQueriesDataType.searchKeywordLabelSelected.name')" text-color="#3A3B3F" background-color="#E3E3E3" />
            </td>
            <td v-if="inboundOption">{{ getLocale(item) }}</td>
            <td :class="props.comparison ? $style.compareData : ''" class="u-taR">
              {{ props.comparison ? valueFormatter(item.comparisonImpressionCount || null) : valueFormatter(item.impressionCount) }}
            </td>
            <td v-if="props.comparison" class="u-taR">{{ valueFormatter(item.impressionCount) }}</td>
            <td v-if="props.comparison" class="u-taR"><KkGrowthRate v-if="item.growthRate" :rate="item.growthRate" /></td>
          </tr>
        </tbody>
      </table>
    </div>
    <template v-if="enableController">
      <button
        v-show="scrollLeft > 0"
        type="button"
        :aria-label="$t('labels.previous')"
        :class="[$style.controller, $style.prev]"
        @mousedown="updateScroll(-10)"
        @mouseup="cancelScroll"
        @mouseleave="cancelScroll"
      >
        <KkIcon name="prev" color="#3A3B3F" />
      </button>
      <button
        v-show="scrollLeft < maxScrollValue"
        type="button"
        :aria-label="$t('labels.next')"
        :class="[$style.controller, $style.next]"
        @mousedown="updateScroll(10)"
        @mouseup="cancelScroll"
        @mouseleave="cancelScroll"
      >
        <KkIcon name="next" color="#3A3B3F" />
      </button>
    </template>
  </div>
</template>

<style lang="scss" module>
@import '../../../../../../stylesheets/biz_spa/foundation/var';
@import '../../../../../../stylesheets/biz_spa/foundation/mixin';

.root {
  position: relative;
}

.clear {
  border-radius: 4px;
  border: 1px solid $color-base-c;
  padding: 4px 16px;
  background: $color-base-a;
  font-size: 12px;
  line-height: 2;
  transition: background 0.3s ease;

  &:hover,
  &:focus-visible {
    background: $color-base-b;
  }
}

.container {
  overflow: auto;
  display: block;
  margin-top: 16px;
  width: 100%;
  max-height: 84vh;
}

.table {
  tbody {
    td:empty {
      display: table-cell;
    }
    td {
      white-space: nowrap;
    }
  }
}

.header {
  position: sticky;
  top: 0;
  z-index: 1;
  tr {
    th {
      &:not(:first-child) {
        border-left: 1px solid $color-base-a;
      }
    }

    &:first-child {
      th:last-child {
        border-bottom-right-radius: 0 !important;
      }
    }

    &:not(:first-child) {
      th {
        border-top: 1px solid $color-base-a;
        border-radius: 0;

        &:first-child {
          border-left: 1px solid $color-base-a;
        }

        &:last-child {
          border-bottom-right-radius: 4px;
        }
      }
    }

    &:nth-child(1):nth-last-child(2) {
      th:last-child {
        border-bottom-right-radius: 4px !important;
      }
    }
  }
}

.sort {
  position: relative;
  padding-right: 20px;

  &::before,
  &::after {
    position: absolute;
    right: 4px;
    content: '';
    border-width: 3px 4px;
    border-style: solid;
  }

  &::before {
    top: 3px;
    border-color: transparent transparent currentColor;
  }

  &::after {
    bottom: 4px;
    border-color: currentColor transparent transparent;
  }

  &.asc {
    &::before {
      border-color: transparent transparent $color-accent-primary;
    }
    &::after {
      border-color: $color-accent-disable transparent transparent;
    }
  }

  &.desc {
    &::before {
      border-color: transparent transparent $color-accent-disable;
    }
    &::after {
      border-color: $color-accent-primary transparent transparent;
    }
  }
}

.compareData {
  background: $color-base-i !important;
}

.divider {
  height: 8px;
}

.head {
  background: #F8F9FA !important;
}

.tag {
  white-space: nowrap;
}

.total {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0 8px;
}

.countWithRate {
  display: flex;
  align-items: baseline;
  justify-content: flex-end;
  white-space: nowrap;
}

.rate {
  font-size: 10px;
}

.count {
  display: block;
  font-size: 18px;
  font-weight: bold;
  text-align: right;
}

.number {
  display: inline-block;
  margin-right: 4px;
  min-width: 2em;
  font-size: 14px;
  font-weight: bold;
}

.text {
  margin-top: 2px;
  text-align: right;
  font-size: 10px;
  font-weight: 1.4;
}

.link {
  display: inline-block;
  margin-top: 2px;
  font-size: 10px;
  font-weight: 1.4;
  text-decoration: underline;

  &:hover,
  &:focus-visible {
    text-decoration: none;
  }
}

.controller {
  position: absolute;
  top: 50%;
  display: none;
  opacity: 0.3;
  transition: opacity 0.3s ease;
  transform: translateY(-50%);

  &:hover,
  &:focus-visible {
    opacity: 0.6;
  }

  &.prev {
    left: -32px;
  }

  &.next {
    right: -32px;
  }

  @include mq() {
    display: block;
  }
}
</style>

<style lang="scss" scoped></style>
